/**
 * chartUtils.js - Chart color scales and utilities
 * Extracted from Analysis.jsx for reuse across chart components
 */

// Sentiment keywords for scale ordering
export const NEGATIVE_KEYWORDS = [
  'strongly disagree', 'disagree', 'very unlikely', 'unlikely',
  'not at all', 'poor', 'very poor', 'negative', 'unfavorable',
  'very unfavorable', 'dissatisfied', 'very dissatisfied', 'low', 'very low'
];

export const POSITIVE_KEYWORDS = [
  'strongly agree', 'agree', 'very likely', 'likely',
  'excellent', 'very good', 'good', 'positive', 'favorable',
  'very favorable', 'satisfied', 'very satisfied', 'high', 'very high'
];

export const NEUTRAL_KEYWORDS = [
  'neutral', 'neither', 'undecided', 'unsure', 'moderate', 'average', 'fair'
];

// Base 5-point scale colors (semantic diverging — coral → slate → teal)
// Aligned with SEMANTIC_HEX in lib/colors.js for visual coherence with the rest of the app.
export const SCALE_COLORS_5 = ['#ef4444', '#fda4af', '#94a3b8', '#5eead4', '#0d9488'];

// Extended 7-point scale colors (deeper extremes)
export const SCALE_COLORS_7 = ['#dc2626', '#ef4444', '#fda4af', '#94a3b8', '#5eead4', '#14b8a6', '#0d9488'];

/**
 * Get appropriate color scale based on number of options
 * @param {string[]} responseScale - Array of response options
 * @returns {string[]} Array of hex colors
 */
export const getScaleColors = (responseScale) => {
  const length = responseScale?.length || 5;
  if (length <= 5) return SCALE_COLORS_5;
  if (length <= 7) return SCALE_COLORS_7;
  return interpolateColors(SCALE_COLORS_7, length);
};

/**
 * Interpolate colors for scales longer than 7 options
 * @param {string[]} baseColors - Base color array to interpolate from
 * @param {number} targetLength - Target number of colors needed
 * @returns {string[]} Interpolated color array
 */
export const interpolateColors = (baseColors, targetLength) => {
  if (targetLength <= baseColors.length) return baseColors.slice(0, targetLength);
  const result = [];
  for (let i = 0; i < targetLength; i++) {
    const idx = Math.floor((i / (targetLength - 1)) * (baseColors.length - 1));
    result.push(baseColors[Math.min(idx, baseColors.length - 1)]);
  }
  return result;
};

/**
 * Get color for a specific response option
 * @param {string} response - The response option
 * @param {string[]} responseScale - Full response scale array
 * @param {number} index - Fallback index if not found in scale
 * @returns {string} Hex color code
 */
export const getResponseColor = (response, responseScale, index) => {
  const colors = getScaleColors(responseScale);
  const scaleIndex = responseScale?.indexOf(response);
  if (scaleIndex !== -1 && scaleIndex < colors.length) {
    return colors[scaleIndex];
  }
  return colors[index % colors.length];
};

/**
 * Order scale options by sentiment (negative → neutral → positive)
 * @param {string[]} responseScale - Array of response options
 * @returns {string[]} Ordered response options
 */
export const getOrderedScale = (responseScale) => {
  if (!responseScale || responseScale.length === 0) return [];
  
  const scored = responseScale.map((option, originalIndex) => {
    const lower = option.toLowerCase().trim();
    let score = 50; // neutral default
    
    // Check negative keywords (lower score)
    if (NEGATIVE_KEYWORDS.some(k => lower.includes(k))) {
      if (lower.includes('strongly') || lower.includes('very')) score = 10;
      else score = 30;
    }
    // Check positive keywords (higher score)
    else if (POSITIVE_KEYWORDS.some(k => lower.includes(k))) {
      if (lower.includes('strongly') || lower.includes('very')) score = 90;
      else score = 70;
    }
    // Check neutral
    else if (NEUTRAL_KEYWORDS.some(k => lower.includes(k))) {
      score = 50;
    }
    
    return { option, score, originalIndex };
  });
  
  // Sort by score (negative first, positive last)
  scored.sort((a, b) => a.score - b.score);
  return scored.map(s => s.option);
};

/**
 * Smart ordering for demographic values with pattern detection
 * @param {string[]} values - Array of demographic values
 * @param {string} columnName - Name of the demographic column
 * @returns {string[]} Ordered values
 */
export const getOrderedDemographicValues = (values, columnName) => {
  if (!values || values.length === 0) return [];
  
  const lowerName = columnName?.toLowerCase() || '';
  
  // Helper function to extract numeric value with special handling
  const extractNumber = (str) => {
    const s = str.toLowerCase().trim();
    // Handle "<X" - return negative to sort first
    if (s.startsWith('<')) return parseInt(s.match(/\d+/)?.[0] || '0') - 0.5;
    // Handle ">X" or "X+" - return large number to sort last
    if (s.startsWith('>') || s.endsWith('+')) return parseInt(s.match(/\d+/)?.[0] || '999') + 0.5;
    // Extract first number from range like "1-3", "5-10", etc.
    const match = s.match(/(\d+)/);
    return match ? parseInt(match[1]) : 999;
  };

  // Age-related columns
  if (lowerName.includes('age') || lowerName.includes('età') || lowerName.includes('fascia')) {
    return [...values].sort((a, b) => extractNumber(a) - extractNumber(b));
  }
  
  // Tenure/Experience/Years/Service
  if (lowerName.includes('tenure') || lowerName.includes('experience') || 
      lowerName.includes('years') || lowerName.includes('anni') ||
      lowerName.includes('service') || lowerName.includes('anzianità') ||
      lowerName.includes('seniority') || lowerName.includes('permanenza')) {
    return [...values].sort((a, b) => extractNumber(a) - extractNumber(b));
  }
  
  // Income/Salary columns
  if (lowerName.includes('income') || lowerName.includes('salary') || 
      lowerName.includes('reddito') || lowerName.includes('stipendio') ||
      lowerName.includes('compensation') || lowerName.includes('pay')) {
    return [...values].sort((a, b) => extractNumber(a) - extractNumber(b));
  }
  
  // Default: alphabetical
  return [...values].sort((a, b) => a.localeCompare(b));
};
